/**
 * The wizard's reads and calls. Each step's preview and apply are the `bootstrap.*` routes; an
 * apply passes `undoable(<route>)` so 2.L's toast also offers Undo for ten seconds. Reads are
 * cached under `['bootstrap', …]`.
 */
import {
  type BootstrapLedgerV1,
  type BootstrapSessionsV1,
  type BootstrapStatusV1,
  bootstrapLedgerApply,
  bootstrapLedgerPreview,
  bootstrapSessionsApply,
  bootstrapSessionsPreview,
  bootstrapStatusApply,
  bootstrapStatusPreview,
  catalogueQuery,
  docsTitles,
  secretsList,
  sessionsList,
  typesList,
  undoBatchesList,
} from '@codex/contracts';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { api } from '../auth/client.js';
import { sessionsKey as ledgerSessionsKey } from '../ledger/sessions.js';
import { SESSIONS_KEY } from '../sessions/queries.js';
import { undoable } from '../undo/undoable.js';
import type { TargetRow } from './model/targets.js';

export const SESSIONS_KIND = 'bootstrap.sessions.apply';
export const STATUS_KIND = 'bootstrap.status.apply';
export const LEDGER_KIND = 'bootstrap.ledger.apply';

export const batchesKey = (kind: string) => ['bootstrap', 'batches', kind] as const;
export const bootstrapSessionsKey = ['bootstrap', 'sessions'] as const;
/** Every target list of the ledger step (its source and type follow). */
export const targetsKey = ['bootstrap', 'targets'] as const;

/**
 * A step's two calls, over its request: the preview, and the apply with the preview's hash; and
 * the reads its apply and the apply's Undo change, read again after each (G8 preview fix: the
 * shell's 30 s staleTime otherwise kept step 4's As of without a session just applied). The
 * step's own batches are always read again.
 */
export interface StepApi<Req> {
  readonly kind: string;
  readonly touches: readonly QueryKey[];
  preview(request: Req): ReturnType<typeof previewSessions>;
  apply(request: Req, previewHash: string): ReturnType<typeof applySessions>;
}

const previewSessions = (body: BootstrapSessionsV1) => api(bootstrapSessionsPreview, { body });
const applySessions = (body: BootstrapSessionsV1, previewHash: string) =>
  api(bootstrapSessionsApply, { body: { ...body, previewHash } }, undoable(SESSIONS_KIND));

/** Sessions created or restored (and removed by the Undo): every list of sessions. */
export const sessionsApi: StepApi<BootstrapSessionsV1> = {
  kind: SESSIONS_KIND,
  touches: [bootstrapSessionsKey, SESSIONS_KEY, ledgerSessionsKey],
  preview: previewSessions,
  apply: applySessions,
};

/** Pages' canon status and play state: the ledger step's page rows show the state. */
export const statusApi: StepApi<BootstrapStatusV1> = {
  kind: STATUS_KIND,
  touches: [targetsKey],
  preview: (body) => api(bootstrapStatusPreview, { body }),
  apply: (body, previewHash) =>
    api(bootstrapStatusApply, { body: { ...body, previewHash } }, undoable(STATUS_KIND)),
};

/** Secrets known and pages known or played: the ledger step's own rows. */
export const ledgerApi: StepApi<BootstrapLedgerV1> = {
  kind: LEDGER_KIND,
  touches: [targetsKey],
  preview: (body) => api(bootstrapLedgerPreview, { body }),
  apply: (body, previewHash) =>
    api(bootstrapLedgerApply, { body: { ...body, previewHash } }, undoable(LEDGER_KIND)),
};

/** A step's batches, newest first (`undo.batches.list` by kind). */
export function useBatches(kind: string) {
  return useQuery({
    queryKey: batchesKey(kind),
    queryFn: async () => (await api(undoBatchesList, { query: { kind, limit: 20 } })).items,
    retry: false,
  });
}

/** The sessions the app holds, paged until the end (the As-of menu lists them all). */
export function useSessions() {
  return useQuery({
    queryKey: bootstrapSessionsKey,
    queryFn: async () => {
      const items = [];
      let cursor: string | undefined;
      do {
        const page = await api(sessionsList, {
          query: { limit: 200, ...(cursor ? { cursor } : {}) },
        });
        items.push(...page.items);
        cursor = page.next ?? undefined;
      } while (cursor !== undefined);
      return items;
    },
    retry: false,
  });
}

export function useTypes() {
  return useQuery({
    queryKey: ['bootstrap', 'types'] as const,
    queryFn: async () => (await api(typesList)).items,
    retry: false,
  });
}

/** Every secret, paged until the end, with its holder page's title. */
export async function loadSecretRows(): Promise<TargetRow[]> {
  const secrets = [];
  let cursor: string | undefined;
  do {
    const page = await api(secretsList, { query: { limit: 200, ...(cursor ? { cursor } : {}) } });
    secrets.push(...page.items);
    cursor = page.next ?? undefined;
  } while (cursor !== undefined);
  const holders = [...new Set(secrets.map((s) => s.documentId))];
  const titles = new Map<string, string>();
  for (let i = 0; i < holders.length; i += 500) {
    const found = await api(docsTitles, { body: { ids: holders.slice(i, i + 500) } });
    for (const t of found.titles) if (t.title !== null) titles.set(t.id, t.title);
  }
  return secrets
    .filter((s) => s.retiredAt === null && s.orphanedAt === null)
    .map((s) => ({
      key: `secret:${s.id}`,
      kind: 'secret',
      id: s.id,
      title: s.label ?? s.text.slice(0, 80),
      detail: titles.get(s.documentId) ?? '',
      state: s.state,
    }));
}

/** Every page of a type, paged until the end. */
export async function loadPageRows(typeKey: string, typeName: string): Promise<TargetRow[]> {
  const rows: TargetRow[] = [];
  let cursor: string | undefined;
  do {
    const page = await api(catalogueQuery, {
      params: { typeKey },
      body: { filters: [], sort: [], limit: 200, ...(cursor ? { cursor } : {}) },
    });
    for (const r of page.items) {
      const first = (column: string) => r.values[column]?.[0];
      const state = first('playState') ?? first('discovery');
      rows.push({
        key: `document:${r.document.id}`,
        kind: 'document',
        id: r.document.id,
        title: r.document.title,
        detail: typeName,
        state: typeof state === 'string' ? state : 'none',
      });
    }
    cursor = page.next ?? undefined;
  } while (cursor !== undefined);
  return rows;
}
